import React, { useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

export default function ColorSetDialog({ colorMeta, items, cars, onClose }) {
  const [historyByItem, setHistoryByItem] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const colorItems = useMemo(() => {
    if (!colorMeta) return [];
    return (items || []).filter((it) => it.color === colorMeta.code).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [colorMeta, items]);

  const carById = useMemo(() => Object.fromEntries((cars || []).map((c) => [c.id, c])), [cars]);

  const fmtTs = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const HH = String(d.getHours()).padStart(2, "0");
      const MM = String(d.getMinutes()).padStart(2, "0");
      return `${dd}/${mm}/${d.getFullYear()} ${HH}:${MM}`;
    } catch { return s; }
  };

  const loadHistory = async (itemId) => {
    if (historyByItem[itemId]) {
      setExpandedId(expandedId === itemId ? null : itemId);
      return;
    }
    setLoadingId(itemId);
    try {
      const r = await api.get(`/inventory/${itemId}/history`);
      setHistoryByItem({ ...historyByItem, [itemId]: r.data || [] });
      setExpandedId(itemId);
    } catch {
      setHistoryByItem({ ...historyByItem, [itemId]: [] });
      setExpandedId(itemId);
    } finally { setLoadingId(null); }
  };

  if (!colorMeta) return null;

  return (
    <Dialog open={!!colorMeta} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="color-set-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full" style={{ background: colorMeta.hex }}/>
            Setul {colorMeta.label} <span className="text-zinc-500 font-normal">({colorItems.length} echipament{colorItems.length === 1 ? "" : "e"})</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto -mx-6 px-6">
          {colorItems.length === 0 ? (
            <div className="text-sm text-zinc-500 italic py-6 text-center">
              Niciun echipament în setul {colorMeta.label}.
            </div>
          ) : (
            <ul className="space-y-2">
              {colorItems.map((it) => {
                const car = it.recipient_car_id ? carById[it.recipient_car_id] : null;
                const isExpanded = expandedId === it.id;
                const history = historyByItem[it.id];
                return (
                  <li key={it.id} className="border rounded-lg overflow-hidden" data-testid={`color-set-item-${it.id}`} style={{ borderColor: colorMeta.hex + "55" }}>
                    <div className="p-3 flex items-start justify-between gap-3 bg-white hover:bg-zinc-50 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{it.name}</span>
                          {it.serial ? (
                            <span className="text-[10px] font-mono text-zinc-500">SN: {it.serial}</span>
                          ) : it.no_serial ? (
                            <span className="text-[10px] italic text-zinc-400">fără serie</span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400 text-amber-900 font-bold">⚠️ LIPSĂ SN</span>
                          )}
                          <span className="text-[11px] text-zinc-500">📍 {it.location || "pe drum / la mașină"}</span>
                          {it.shelf_col && it.shelf_row && (
                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 font-mono">{it.shelf_row}{it.shelf_col}</span>
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-600 mt-1.5 space-y-0.5">
                          {it.assigned_at && (
                            <div>
                              <strong>Ultima atribuire:</strong> {fmtTs(it.assigned_at)}
                              {it.assigned_by && <> · <strong>dat de</strong> {it.assigned_by}</>}
                            </div>
                          )}
                          {it.recipient_car_label && (
                            <div className="text-emerald-700">
                              🚗 <strong>Către mașină:</strong> {it.recipient_car_label}
                              {car && car.tool_set_color && <> ({car.tool_set_color})</>}
                            </div>
                          )}
                          {it.assignee && (
                            <div className="text-blue-700">
                              👤 <strong>Asignat:</strong> {it.assignee}
                            </div>
                          )}
                          {it.recipient_note && (
                            <div className="italic text-zinc-500">📝 {it.recipient_note}</div>
                          )}
                          {!it.assigned_at && !it.recipient_car_label && !it.assignee && (
                            <div className="italic text-zinc-400">Nu există date de alocare. Mută item-ul pentru a tracking.</div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => loadHistory(it.id)}
                        className="text-xs px-2 py-1 rounded border border-zinc-300 text-zinc-700 hover:bg-zinc-100 flex-shrink-0 self-start"
                        disabled={loadingId === it.id}
                        data-testid={`color-set-history-${it.id}`}
                      >
                        {loadingId === it.id ? "..." : isExpanded ? "▾ Ascunde istoric" : "▸ Istoric mutări"}
                      </button>
                    </div>

                    {isExpanded && history && (
                      <div className="border-t bg-zinc-50/80 p-3 text-xs space-y-2 max-h-[300px] overflow-auto" data-testid={`color-set-history-list-${it.id}`}>
                        {history.length === 0 ? (
                          <div className="italic text-zinc-400 text-center py-3">Niciun istoric.</div>
                        ) : (
                          history.map((h, idx) => (
                            <div key={h.id || idx} className="bg-white border border-zinc-200 rounded p-2 space-y-1">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <span className="font-mono text-zinc-700">{fmtTs(h.ts)}</span>
                                <span className="text-zinc-500">{h.action || "—"}{h.user && <> · <strong className="text-zinc-700">de {h.user}</strong></>}</span>
                              </div>
                              <div className="text-zinc-600 space-y-0.5">
                                {(h.from_location || h.to_location) && (
                                  <div>
                                    Locație: <span className="font-mono">{h.from_location || "—"}</span> → <span className="font-mono font-bold text-emerald-700">{h.to_location || "—"}</span>
                                  </div>
                                )}
                                {(h.from_color || h.to_color) && h.from_color !== h.to_color && (
                                  <div>Culoare: {h.from_color || "—"} → <strong>{h.to_color || "—"}</strong></div>
                                )}
                                {h.to_car_label && (
                                  <div className="text-emerald-700">🚗 Către mașină: <strong>{h.to_car_label}</strong></div>
                                )}
                                {h.to_assignee && (
                                  <div className="text-blue-700">👤 Asignat: <strong>{h.to_assignee}</strong></div>
                                )}
                                {h.recipient_note && (
                                  <div className="italic text-zinc-500">📝 {h.recipient_note}</div>
                                )}
                                {h.reason && (
                                  <div className="italic text-amber-700">💬 Motiv: {h.reason}</div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="color-set-close">Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
